import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useGetApplicantInformation,
  useUpdateApplicantInformation,
  type ApplicantInformation,
} from './graphql';
import { UserInformationForm } from './components';
import {
  Alert,
  type AlertDetails,
  ErrorPage,
  Loading,
  ROLES_MODULE,
  GS_ROLES_MODULE,
} from '../../components';
import { TokenType, useAuth } from '../../contexts';
import { VIEW_MODE } from '../../utils';

export const UserInformation: React.FunctionComponent = () => {
  const { applicantId } = useParams();
  const { role: currentRole, user } = useAuth();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const isStaffView = user?.tokenType === TokenType.INTERNAL;

  const isGSMember = Object.values(GS_ROLES_MODULE).includes(
    currentRole as GS_ROLES_MODULE,
  );

  const isARMember = currentRole === ROLES_MODULE.RIGHT_AR;

  let viewMode = VIEW_MODE.VIEW_AS_APPLICANT;
  if (isGSMember) viewMode = VIEW_MODE.VIEW_AS_GS_MEMBER;
  if (isARMember) viewMode = VIEW_MODE.VIEW_AS_AR_MEMBER;

  if (isStaffView && !isGSMember && !isARMember) {
    return <ErrorPage errorCode={403} />;
  }

  const { applicant, loading, error } = useGetApplicantInformation(
    applicantId ?? '',
  );

  const updateApplicantInformation = useUpdateApplicantInformation();
  const handleUpdateApplicant = async (
    applicant: ApplicantInformation,
  ): Promise<void> => {
    try {
      const {
        id,
        surname,
        givenName,
        chineseName = '',
        identityDocumentNumber,
        identityDocumentType,
        identityType,
        telephoneNumber,
        disabilitySpecialType,
        disabilitySpecialDetail,
        ...others
      } = applicant;
      const res = await updateApplicantInformation({
        variables: {
          id,
          input: {
            name: { surname, givenName, chineseName },
            identity: {
              identityType,
              identityDocumentNumber,
              identityDocumentType,
            },
            ...(disabilitySpecialType !== 'NA' &&
            disabilitySpecialType !== undefined
              ? {
                  disabilitySpecial: {
                    disabilitySpecialType,
                    disabilitySpecialDetail,
                  },
                }
              : { disabilitySpecial: null }),
            telephoneNumber: !telephoneNumber ? '' : telephoneNumber, // to override the previously value
            ...others,
          },
        },
      });

      if (res?.data?.updateApplicant?._id === id) {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: 'User Information Updated Successfully',
          alertType: 'success',
        });
      }
    } catch (error) {
      setOpenAlert(true);
      setAlertDetails({
        alertContent: `Error Updating User Information.${
          (error as Error).message
        }`,
        alertType: 'error',
      });
    }
  };

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <UserInformationForm
          initialValues={applicant}
          handleUpdateApplicant={handleUpdateApplicant}
          viewMode={viewMode}
        />
      )}
    </>
  );
};
